import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Search, 
  MapPin, 
  Briefcase, 
  TrendingUp, 
  Globe, 
  ShieldCheck, 
  Code,
  BarChart,
  Palette,
  Layers,
  ArrowRight,
  Plus,
  Minus,
  ChevronRight,
  BookOpen,
  Award,
  Terminal,
  ArrowUpRight
} from 'lucide-react';
import LogoTicker from '../common/LogoTicker';
import { BentoGrid, BentoCard } from '../common/BentoGrid';
import './LandingPage.css';

const LandingPage = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [locationQuery, setLocationQuery] = useState('');
  const [activeFaq, setActiveFaq] = useState(null);
  const [activeTestimonial, setActiveTestimonial] = useState(0);

  const handleSearch = (e) => {
    e.preventDefault();
    const params = new URLSearchParams();
    if (searchQuery) params.append('q', searchQuery);
    if (locationQuery) params.append('location', locationQuery);
    navigate(`/dashboard?${params.toString()}`);
  };

  // Animation variants
  const fadeInUp = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: [0.16, 1, 0.3, 1] } }
  };

  const staggerContainer = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const spotlightJobs = [
    { title: 'Senior Frontend Engineer', company: 'TechFlow', tags: ['Remote', 'Equity', 'High-Pay'], logo: '⚡', category: 'Engineering' },
    { title: 'Product Designer', company: 'CreativeLab', tags: ['Hybrid', 'Full-time'], logo: '🎨', category: 'Design' },
    { title: 'Data Scientist', company: 'DataMind', tags: ['Remote', 'AI/ML'], logo: '📊', category: 'Data Science' },
  ];

  const testimonials = [
    {
      quote: "Zpluse helped me find a remote role at a top startup within 2 weeks. The matching precision and UI clarity is unlike any other job portal.",
      author: "Sarah Jenkins",
      title: "Senior Product Designer",
      meta: "Hired via TechFlow",
      initials: "SJ",
      color: "var(--brand-500)"
    },
    {
      quote: "As a recruiter, the quality of talent on Zpluse is exceptional. We closed 5 key engineering roles in half the time compared to standard networks.",
      author: "Marcus Chen",
      title: "Talent Acquisition Lead",
      meta: "Recruiter at CreativeLab",
      initials: "MC",
      color: "var(--brand-accent)"
    },
    {
      quote: "The developer dashboard is clean and keeps job tracking effortless. Found my transition to a remote Lead Role here.",
      author: "David Miller",
      title: "DevOps Engineer",
      meta: "Hired via DataMind",
      initials: "DM",
      color: "#60a5fa"
    }
  ];

  const faqData = [
    {
      question: "How does Zplus matching algorithm work?",
      answer: "Zplus analyzes your profile details, past applications, and skills to dynamically match you with verified listings. We highlight high-match scores directly on your feed."
    },
    {
      question: "Can I hide my profile from my current employer?",
      answer: "Yes. In your profile settings, you can enable 'Incognito Mode' which restricts your profile visibility only to verified recruiters where you submit applications."
    },
    {
      question: "What makes Zplus different from standard job boards?",
      answer: "We focus on verified listings, direct-to-recruiter pipelines, and a premium clutter-free interface. We eliminate spam listings and force transparent salaries on postings."
    },
    {
      question: "Is Zplus completely free for job seekers?",
      answer: "Yes, searching, applying, and showcasing your profile on Zplus is 100% free. We charge recruiters to post openings and access our elite talent database."
    }
  ];

  const insights = [
    {
      title: "The Rise of Specialized SaaS Roles: What Recruiters Look For",
      category: "Market Insights",
      readTime: "5 min read",
      date: "June 15, 2026",
      icon: <Terminal size={20} />
    },
    {
      title: "Salary Transparency in 2026: Negotiating Remote Contracts",
      category: "Career Advice",
      readTime: "4 min read",
      date: "June 12, 2026",
      icon: <BookOpen size={20} />
    },
    {
      title: "Mastering the Design Portfolio: Inside the Minds of Product Leads",
      category: "UX Portfolio",
      readTime: "6 min read",
      date: "June 08, 2026",
      icon: <Award size={20} />
    }
  ];

  return (
    <div className="landing-container">
      {/* --- HERO SECTION --- */}
      <section className="hero-wrapper">
        <motion.div 
          initial="hidden"
          animate="visible"
          variants={staggerContainer}
          className="container"
        >
          <div className="clip-text-reveal">
            <motion.h1 
              initial={{ y: "100%", rotate: "5deg" }}
              animate={{ y: 0, rotate: 0 }}
              transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
              className="hero-massive-text"
            >
              Premium talent.<br />
              <span>High-impact roles.</span>
            </motion.h1>
          </div>

          <motion.p variants={fadeInUp} className="hero-subtext mx-auto">
            Get fully dedicated matching pipelines. Spared of clutter, optimized for speed, and styled to compete with the web's best interfaces.
          </motion.p>

          <motion.div variants={fadeInUp} className="search-bar-wrapper">
            <form onSubmit={handleSearch} className="search-bar-container mx-auto">
              <div className="search-input-wrapper">
                <Search className="search-icon" size={20} />
                <input 
                  type="text" 
                  placeholder="Job Title, Skills, or Keyword" 
                  className="search-field"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
              <div className="search-input-wrapper">
                <MapPin className="search-icon" size={20} />
                <input 
                  type="text" 
                  placeholder="Location or Remote" 
                  className="search-field"
                  value={locationQuery}
                  onChange={(e) => setLocationQuery(e.target.value)}
                />
              </div>
              <button type="submit" className="search-submit-btn">
                <span>Find Jobs</span>
                <ArrowRight size={18} />
              </button>
            </form>
          </motion.div>

          <motion.div variants={fadeInUp} className="hero-trust-badges mt-5 d-flex flex-wrap justify-content-center gap-4 opacity-50">
            <div className="d-flex align-items-center gap-2">
              <TrendingUp size={16} /> <span>10k+ Active Jobs</span>
            </div>
            <div className="d-flex align-items-center gap-2">
              <Globe size={16} /> <span>Remote First Funnels</span>
            </div>
            <div className="d-flex align-items-center gap-2">
              <Briefcase size={16} /> <span>Elite Talent Network</span>
            </div>
          </motion.div>
        </motion.div>
      </section>

      {/* --- BRAND TICKER --- */}
      <LogoTicker />

      {/* --- SPOTLIGHT OPPORTUNITIES --- */}
      <section className="py-5 px-3 bg-dark-section">
        <div className="container py-5">
          <div className="section-header">
            <span className="section-tag">Curated openings</span>
            <h2 className="section-title">Spotlight Opportunities</h2>
          </div>

          <motion.div 
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="jobs-grid"
          >
            {spotlightJobs.map((job, index) => (
              <motion.article 
                key={index} 
                variants={fadeInUp}
                className="job-card-premium"
                onClick={() => navigate(`/dashboard?q=${job.title}`)}
              >
                <div className="d-flex justify-content-between align-items-start mb-4">
                  <div className="job-logo-wrapper">{job.logo}</div>
                  <div className="job-badge-row">
                    {job.tags.map((tag, tIndex) => (
                      <span key={tIndex} className={`badge-premium ${tag === 'High-Pay' ? 'high-pay' : ''}`}>
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
                <div className="job-card-category">{job.category}</div>
                <h3 className="h4 font-bold mb-1 card-title">{job.title}</h3>
                <p className="text-gray-400 mb-4">{job.company}</p>
                <div className="view-details-btn d-flex align-items-center gap-2">
                  <span>View Details</span>
                  <ArrowRight size={16} className="arrow" />
                </div>
              </motion.article>
            ))}
          </motion.div>
        </div>
      </section>

      {/* --- BENTO CATEGORIES --- */}
      <section className="py-5 px-3 bg-light-section">
        <div className="container py-5">
          <div className="section-header">
            <span className="section-tag">Browse by expertise</span>
            <h2 className="section-title">Explore Premium Categories</h2>
          </div>

          <BentoGrid>
            {/* DevOps - col 2 */}
            <BentoCard colSpan="col-span-1 md:col-span-2" rowSpan="row-span-1" delay={0}>
              <div className="d-flex flex-column h-100 justify-content-between">
                <div className="bento-header">
                  <div className="bento-icon-wrapper"><Layers size={24} /></div>
                  <h3 className="h4 font-bold mb-2">DevOps</h3>
                  <p className="text-gray-500 text-sm">Kubernetes, Docker, Cloud AWS/GCP, Infrastructure as Code.</p>
                </div>
                <div className="d-flex justify-content-between align-items-center pt-3 mt-auto border-top border-light">
                  <span className="text-sm font-bold text-gray-400">1.2k+ Active Openings</span>
                  <button onClick={() => navigate('/dashboard?q=DevOps')} className="bento-arrow-btn">
                    <ChevronRight size={18} />
                  </button>
                </div>
              </div>
            </BentoCard>

            {/* Design - col 1 */}
            <BentoCard colSpan="col-span-1" rowSpan="row-span-1" delay={0.1}>
              <div className="d-flex flex-column h-100 justify-content-between">
                <div className="bento-header">
                  <div className="bento-icon-wrapper"><Palette size={24} /></div>
                  <h3 className="h4 font-bold mb-2">Design</h3>
                  <p className="text-gray-500 text-sm">UI/UX, Product, Motion.</p>
                </div>
                <div className="d-flex justify-content-between align-items-center pt-3 mt-auto border-top border-light">
                  <span className="text-sm font-bold text-gray-400">600+ Jobs</span>
                  <button onClick={() => navigate('/dashboard?q=Design')} className="bento-arrow-btn">
                    <ChevronRight size={18} />
                  </button>
                </div>
              </div>
            </BentoCard>

            {/* Data Science - col 1 */}
            <BentoCard colSpan="col-span-1" rowSpan="row-span-1" delay={0.2}>
              <div className="d-flex flex-column h-100 justify-content-between">
                <div className="bento-header">
                  <div className="bento-icon-wrapper"><BarChart size={24} /></div>
                  <h3 className="h4 font-bold mb-2">Data Science</h3>
                  <p className="text-gray-500 text-sm">Python, ML, Analytics.</p>
                </div>
                <div className="d-flex justify-content-between align-items-center pt-3 mt-auto border-top border-light">
                  <span className="text-sm font-bold text-gray-400">850+ Jobs</span>
                  <button onClick={() => navigate('/dashboard?q=Data Science')} className="bento-arrow-btn">
                    <ChevronRight size={18} />
                  </button>
                </div>
              </div>
            </BentoCard>

            {/* Engineering - col 2 */}
            <BentoCard colSpan="col-span-1 md:col-span-2" rowSpan="row-span-1" delay={0.3} dark={true}>
              <div className="d-flex flex-column h-100 justify-content-between">
                <div className="bento-header">
                  <div className="bento-icon-wrapper text-dark" style={{ background: 'var(--brand-500)' }}><Code size={24} /></div>
                  <h3 className="h4 font-bold mb-2 text-white">Software Engineering</h3>
                  <p className="text-gray-400 text-sm">React, Node, Python, System Architectures, High-Performance Systems.</p>
                </div>
                <div className="d-flex justify-content-between align-items-center pt-3 mt-auto border-top" style={{ borderColor: 'rgba(255,255,255,0.05)' }}>
                  <span className="text-sm font-bold text-brand">2.4k+ Elite Listings</span>
                  <button onClick={() => navigate('/dashboard?q=Engineering')} className="bento-arrow-btn dark">
                    <ChevronRight size={18} />
                  </button>
                </div>
              </div>
            </BentoCard>

            {/* Stats cell 1 */}
            <BentoCard colSpan="col-span-1 md:col-span-2" rowSpan="row-span-1" delay={0.4} className="bg-gradient-green text-dark">
              <div className="d-flex flex-column h-100 justify-content-center">
                <h3 className="display-4 font-bold mb-1 tracking-tight" style={{ fontWeight: 900 }}>100%</h3>
                <p className="lead font-bold mb-0">Verified Companies & Salaries</p>
                <p className="text-sm opacity-75">No ghost postings. Direct-to-recruiter channel.</p>
              </div>
            </BentoCard>
          </BentoGrid>
        </div>
      </section>

      {/* --- DATA-DRIVEN STATS & TEAM BLOCK --- */}
      <section className="py-5 px-3 bg-dark-section border-top border-bottom" style={{ borderColor: 'rgba(255, 255, 255, 0.05)' }}>
        <div className="container py-5">
          <div className="row align-items-center g-5">
            <div className="col-lg-6">
              <div className="stats-info-block">
                <span className="section-tag left">Our Network Stats</span>
                <h2 className="section-title text-start mb-4">When we say Zplus is data-driven, we mean it</h2>
                <p className="lead text-gray-400 mb-4">
                  We verify each job posting, benchmark salaries, and run predictive candidate matches so that your job search dashboard does the hard work for you.
                </p>
                <button onClick={() => navigate('/register')} className="btn-warning d-inline-flex align-items-center gap-2 py-3 px-4 rounded-pill border-0 font-bold">
                  <span>Join Zplus Network</span>
                  <ArrowRight size={18} />
                </button>
              </div>
            </div>
            <div className="col-lg-6">
              <div className="row g-4">
                <div className="col-sm-6">
                  <div className="stat-card">
                    <h3 className="stat-number">10k+</h3>
                    <p className="stat-label">Active verified listings</p>
                  </div>
                </div>
                <div className="col-sm-6">
                  <div className="stat-card">
                    <h3 className="stat-number">40+</h3>
                    <p className="stat-label">Elite hiring partners</p>
                  </div>
                </div>
                <div className="col-12">
                  <div className="team-image-card">
                    <div className="team-image-overlay">
                      <h4 className="h5 mb-1 text-white">Elite Recruiting Hub</h4>
                      <p className="text-sm text-brand mb-0">Connecting engineers directly to CTOs</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* --- COLLABORATIVE TESTIMONIALS SLIDER --- */}
      <section className="py-5 px-3 bg-light-section">
        <div className="container py-5">
          <div className="section-header">
            <span className="section-tag">Collaborative to the core</span>
            <h2 className="section-title">Real success stories from Zplus</h2>
          </div>

          <div className="testimonial-slider-container">
            <AnimatePresence mode="wait">
              <motion.div
                key={activeTestimonial}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.4 }}
                className="testimonial-slide row align-items-center g-5"
              >
                <div className="col-md-5">
                  <div className="slide-image-frame d-flex align-items-center justify-content-center" style={{ borderColor: testimonials[activeTestimonial].color }}>
                    <div className="display-1 font-bold text-muted opacity-50" style={{ color: testimonials[activeTestimonial].color }}>
                      {testimonials[activeTestimonial].initials}
                    </div>
                  </div>
                </div>
                <div className="col-md-7">
                  <div className="slide-content">
                    <div className="quote-mark">“</div>
                    <p className="lead font-medium mb-4 text-gray-800">
                      {testimonials[activeTestimonial].quote}
                    </p>
                    <div>
                      <h4 className="h5 font-bold mb-0 text-gray-900">{testimonials[activeTestimonial].author}</h4>
                      <p className="text-gray-500 mb-0">{testimonials[activeTestimonial].title}</p>
                      <span className="text-sm font-bold mt-2 d-inline-block text-brand">{testimonials[activeTestimonial].meta}</span>
                    </div>
                  </div>
                </div>
              </motion.div>
            </AnimatePresence>

            <div className="slider-navigation mt-5 d-flex gap-2">
              {testimonials.map((_, idx) => (
                <button
                  key={idx}
                  onClick={() => setActiveTestimonial(idx)}
                  className={`slider-dot ${activeTestimonial === idx ? 'active' : ''}`}
                />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* --- FAQ ACCORDION --- */}
      <section className="py-5 px-3 bg-dark-section border-top" style={{ borderColor: 'rgba(255, 255, 255, 0.05)' }}>
        <div className="container py-5">
          <div className="row g-5">
            <div className="col-lg-4">
              <span className="section-tag left">Common questions</span>
              <h2 className="section-title text-start mb-3">FAQ</h2>
              <p className="text-gray-400">Everything you need to know about Zplus job matching pipelines, profile visibility, and employer channels.</p>
            </div>
            <div className="col-lg-8">
              <div className="faq-list d-flex flex-column gap-3">
                {faqData.map((faq, index) => {
                  const isOpen = activeFaq === index;
                  return (
                    <div 
                      key={index}
                      className={`faq-item ${isOpen ? 'open' : ''}`}
                      onClick={() => setActiveFaq(isOpen ? null : index)}
                    >
                      <div className="faq-question-row d-flex justify-content-between align-items-center">
                        <h4 className="h5 mb-0 text-white font-medium">{faq.question}</h4>
                        <div className="faq-toggle-icon">
                          {isOpen ? <Minus size={18} /> : <Plus size={18} />}
                        </div>
                      </div>
                      <AnimatePresence initial={false}>
                        {isOpen && (
                          <motion.div
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: 'auto', opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            transition={{ duration: 0.3, ease: 'easeInOut' }}
                            className="faq-answer-container"
                          >
                            <p className="faq-answer text-gray-400 mb-0 pt-3">{faq.answer}</p>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* --- FRESH INSIGHTS GRID --- */}
      <section className="py-5 px-3 bg-light-section border-top">
        <div className="container py-5">
          <div className="section-header">
            <span className="section-tag">Career Resources</span>
            <h2 className="section-title text-gray-900">Fresh insights by our team</h2>
          </div>

          <div className="row g-4">
            {insights.map((post, idx) => (
              <div key={idx} className="col-md-4">
                <article className="insight-card">
                  <div className="insight-image-frame d-flex align-items-center justify-content-center">
                    <div className="insight-icon-box">{post.icon}</div>
                  </div>
                  <div className="insight-content p-4">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <span className="insight-category">{post.category}</span>
                      <span className="insight-time text-gray-400">{post.readTime}</span>
                    </div>
                    <h3 className="h4 font-bold text-gray-900 mb-3">{post.title}</h3>
                    <div className="d-flex justify-content-between align-items-center mt-4">
                      <span className="insight-date text-gray-400">{post.date}</span>
                      <div className="insight-link-arrow">
                        <ArrowUpRight size={18} />
                      </div>
                    </div>
                  </div>
                </article>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* --- SECURITY TRUST --- */}
      <div className="security-mention">
        <ShieldCheck size={18} />
        <span>Enterprise-grade security. Verified SSL Protected. Your data is always encrypted.</span>
      </div>
    </div>
  );
};

export default LandingPage;
